// src/maintenanceAccess.js
//
// Single source of truth for the maintenance hub's role → category map.
// Keeping the rules in one tiny module means the sidebar filter, the
// per-route gate, and any future audit / docs all agree on what
// "maintenance-L1" actually buys you.
//
// Tiers (subdivide the legacy single `maintenance` role):
//   maintenance-l1 → IN-SUITE MAINTENANCE only
//   maintenance-l2 → IN-SUITE MAINTENANCE + ADMIN
//   maintenance-l3 → everything (in-suite, admin, service, finance, discovery)
//
// Legacy `maintenance` (no tier suffix) is treated as full access (= L3)
// so accounts created before this rolled out continue to see every tab.

export const CAT_INSUITE = 'insuite';
export const CAT_SERVICE = 'service';
export const CAT_ADMIN = 'admin';
// Money that leaves the company: affiliate commission rates and payouts.
// Deliberately separate from CAT_ADMIN (which L2 has) and from CAT_SERVICE
// (which is L3-only today but only by coincidence — it exists to describe
// Jobber-backed reporting, so hanging payouts off it would break the moment
// a service tab is opened up to L2).
export const CAT_FINANCE = 'finance';
// Condo registry / building index. Same accounts as full access: L3 and the
// legacy `maintenance` role. L1 and L2 do not get this category.
export const CAT_DISCOVERY = 'discovery';

// Map a role string → set of categories the role can see.
// Returns an empty Set for non-maintenance roles so callers can treat
// "no access" uniformly without an explicit role check.
export function categoriesForRole(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'maintenance-l1') return new Set([CAT_INSUITE]);
  if (r === 'maintenance-l2') return new Set([CAT_INSUITE, CAT_ADMIN]);
  if (r === 'maintenance-l3') return new Set([CAT_INSUITE, CAT_ADMIN, CAT_SERVICE, CAT_FINANCE, CAT_DISCOVERY]);
  // Legacy / catch-all
  if (r === 'maintenance') return new Set([CAT_INSUITE, CAT_ADMIN, CAT_SERVICE, CAT_FINANCE, CAT_DISCOVERY]);
  return new Set();
}

export function hasAnyMaintenanceAccess(role) {
  return categoriesForRole(role).size > 0;
}

// Billing / Jobber details (client link + pricing + schedule action) are
// office-only. L2 and L3 (and the legacy full-access `maintenance` role)
// can see/edit them; L1 field techs cannot. Mirrors the ADMIN category.
export function canSeeBillingDetails(role) {
  return categoriesForRole(role).has(CAT_ADMIN);
}

// Company-wide billing figures — what each building was actually
// invoiced for a cycle — are top-tier only. CAT_SERVICE is the category
// no tier below L3 has, so it doubles as the "is L3" test (and keeps
// legacy tier-less `maintenance` accounts included).
export function canSeeCycleInvoicing(role) {
  return categoriesForRole(role).has(CAT_SERVICE);
}

// Static map of sidebar tab key → category. Lives here (vs. in
// MaintenanceHub) so the per-route CategoryGate can do the lookup
// without having to import the whole hub.
export const TAB_TO_CATEGORY = {
  // In-suite maintenance
  home: CAT_INSUITE,
  'in-suite': CAT_INSUITE,
  'check-ins': CAT_INSUITE,
  schedule: CAT_INSUITE,
  'seasonal-planning': CAT_INSUITE,
  calendar: CAT_INSUITE,
  analytics: CAT_INSUITE,
  // Contract revenue + the add-on work it generates. Sits under the
  // in-suite heading but is L3-only (CAT_SERVICE): it's whole-portfolio
  // revenue, which no tier below L3 should see.
  'contract-revenue': CAT_SERVICE,
  // Service (Jobber-backed)
  'money-overview': CAT_SERVICE,
  'revenue-breakdown': CAT_SERVICE,
  'pnl-calendar': CAT_SERVICE,
  'building-heatmap': CAT_SERVICE,
  // The Service Tracker sits under the Growth heading in the sidebar and is
  // visible to every maintenance tier (L1+): coordinators need it to
  // check their own quotas. CAT_INSUITE is the category all tiers have.
  'quota-tracker': CAT_INSUITE,
  // Mechanical (back-office frame of /mech-schedule) — same
  // visibility as the Service Tracker: every maintenance tier (L1+).
  'mech-management': CAT_INSUITE,
  'mech-calendar': CAT_INSUITE,
  // Company-wide financials (revenue/costs/profit per division).
  // L3-only — same bar as PnL Calendar / Building Heatmap.
  'division-margins': CAT_SERVICE,
  // Admin (L2+)
  'manage-accounts': CAT_ADMIN,
  documents: CAT_ADMIN,
  'crew-fleet': CAT_ADMIN,
  reviews: CAT_ADMIN,
  'building-index': CAT_DISCOVERY,
  // Affiliate commission rates + payouts (L3 only).
  'affiliate-payouts': CAT_FINANCE,
};

export function categoryForTab(tabKey) {
  return TAB_TO_CATEGORY[tabKey] || null;
}

export function canAccessTab(role, tabKey, hiddenTabs = []) {
  const cat = categoryForTab(tabKey);
  if (!cat) return false;
  return categoriesForRole(role).has(cat) && !hiddenTabs.includes(tabKey);
}

// Display groups are intentionally separate from permission categories: Growth,
// for example, contains tabs with three different role ceilings.
export const DASHBOARD_ACCESS_GROUPS = [
  { key: 'today', label: 'Daily Overview', tabs: [{ key: 'home', label: 'Daily Overview' }] },
  { key: 'insuite', label: 'In-Suite Maintenance', tabs: [
    { key: 'in-suite', label: 'Overview' },
    { key: 'check-ins', label: 'Daily Check In' },
    { key: 'schedule', label: 'Schedule' },
    { key: 'seasonal-planning', label: 'Seasonal Planner' },
    { key: 'calendar', label: 'Calendar' },
    { key: 'analytics', label: 'Performance' },
    { key: 'contract-revenue', label: 'Contract Revenue' },
  ] },
  { key: 'mechanical', label: 'Mechanical Maintenance', tabs: [
    { key: 'mech-management', label: 'Overview' },
    { key: 'mech-calendar', label: 'Calendar' },
  ] },
  { key: 'money', label: 'Money', tabs: [
    { key: 'money-overview', label: 'Overview' },
    { key: 'revenue-breakdown', label: 'Revenue Breakdown' },
    { key: 'pnl-calendar', label: 'PnL Calendar' },
    { key: 'building-heatmap', label: 'Building Heatmap' },
    { key: 'division-margins', label: 'Division Margins' },
  ] },
  { key: 'growth', label: 'Growth', tabs: [
    { key: 'reviews', label: 'Reviews' },
    { key: 'quota-tracker', label: 'Service Tracker' },
    { key: 'building-index', label: 'Building Index' },
    { key: 'affiliate-payouts', label: 'Affiliate Marketing' },
  ] },
  { key: 'admin', label: 'Admin', tabs: [
    { key: 'manage-accounts', label: 'Manage Accounts' },
    { key: 'documents', label: 'Documents' },
    { key: 'crew-fleet', label: 'Crew & Fleet' },
  ] },
];

export function tabForMaintenancePath(pathname) {
  if (/^\/maintenance\/money-overview\/(revenue|profit|margin|totalCost|activeCustomers)\/?$/.test(pathname)) return 'money-overview';
  // Jobber Links has no sidebar entry of its own; it opens from a button on
  // the mechanical overview, so it rides that tab's visibility. Its route
  // still carries CategoryGate(CAT_ADMIN), which is what keeps field tiers
  // out — this only decides whether the page counts as somewhere you can be.
  if (/^\/maintenance\/mech-jobber-links\/?$/.test(pathname)) return 'mech-management';
  const match = /^\/maintenance(?:\/([^/]+))?\/?$/.exec(pathname);
  if (!match) return null;
  const key = match[1] || 'home';
  return categoryForTab(key) ? key : null;
}

export function maintenanceTabPath(key) {
  return key === 'home' ? '/maintenance' : `/maintenance/${key}`;
}

// First tab key the given role IS allowed to land on. Used as a safe
// redirect target when an L1/L2 user types a URL they can't see.
// Falls back to 'home' since every tier has it.
export function firstAccessibleTab(role, hiddenTabs = [], allowedTabs = null) {
  return DASHBOARD_ACCESS_GROUPS.flatMap(group => group.tabs)
    .find(tab => canAccessTab(role, tab.key, hiddenTabs)
      && (!allowedTabs || allowedTabs.includes(tab.key)))?.key || null;
}

// Reads the current user out of localStorage the same way
// MaintenanceProtectedRoute does, so every consumer of "what can this
// user see?" routes through the same parsing.
export function readMaintenanceUser() {
  try {
    const token = localStorage.getItem('managerToken');
    const userStr = localStorage.getItem('managerUser');
    if (!token || !userStr) return null;
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}
